/**
 * Pieces jointes : photos et PDF de factures.
 *
 * ## Recompression a l'ajout
 *
 * Une photo d'iPhone brute pese 3 a 5 Mo en 4032 × 3024. Vingt factures, et le
 * justificatif depasse la taille qu'une messagerie accepte ; la base, elle,
 * enfle sur un appareil ou le quota est compte. On ramene donc chaque image a
 * 2000 px sur le grand cote, en JPEG qualite 0.82 : 250 a 450 Ko, parfaitement
 * lisible une fois imprimee en A4.
 *
 * La conversion regle aussi le format : pdf-lib n'integre que JPEG et PNG,
 * or l'appareil photo d'iOS produit du HEIC. Passer par un canvas convertit
 * tout ce que le navigateur sait afficher.
 */

import { nowInstant } from '../domain/temps'
import type { Id, Piece } from '../domain/types'
import { db, nouvelId } from './db'

export const COTE_MAX_PX = 2000
export const QUALITE_JPEG = 0.82
export const TAILLE_MAX_PDF = 15 * 1024 * 1024

export type MetaPiece = Omit<Piece, 'blob'>

export class PieceRefusee extends Error {}

export async function ajouterPiece(operationId: Id, fichier: File): Promise<Id> {
  const id = nouvelId()
  const base = { id, operationId, nom: fichier.name || 'facture', ajouteeLe: nowInstant() }

  if (fichier.type === 'application/pdf' || /\.pdf$/i.test(fichier.name)) {
    if (fichier.size > TAILLE_MAX_PDF) {
      throw new PieceRefusee('Ce PDF dépasse 15 Mo : il alourdirait trop le justificatif.')
    }
    await db.pieces.add({ ...base, type: 'application/pdf', blob: fichier, largeur: 0, hauteur: 0, octets: fichier.size })
    return id
  }

  if (!fichier.type.startsWith('image/') && !/\.(heic|heif|jpe?g|png|webp)$/i.test(fichier.name)) {
    throw new PieceRefusee('Seules les photos et les PDF peuvent être joints.')
  }

  const { blob, largeur, hauteur } = await recompresser(fichier)
  await db.pieces.add({
    ...base,
    nom: base.nom.replace(/\.(heic|heif|png|webp)$/i, '.jpg'),
    type: 'image/jpeg',
    blob,
    largeur,
    hauteur,
    octets: blob.size,
  })
  return id
}

async function recompresser(fichier: Blob): Promise<{ blob: Blob; largeur: number; hauteur: number }> {
  let source: ImageBitmap | HTMLImageElement
  try {
    // `imageOrientation: 'from-image'` applique la rotation EXIF : sans elle,
    // une facture photographiee en portrait ressortirait couchee dans le PDF.
    source = await createImageBitmap(fichier, { imageOrientation: 'from-image' })
  } catch {
    source = await chargerImage(fichier)
  }

  const l0 = 'naturalWidth' in source ? source.naturalWidth : source.width
  const h0 = 'naturalHeight' in source ? source.naturalHeight : source.height
  if (!l0 || !h0) throw new PieceRefusee('Image illisible.')

  const echelle = Math.min(1, COTE_MAX_PX / Math.max(l0, h0))
  const largeur = Math.round(l0 * echelle)
  const hauteur = Math.round(h0 * echelle)

  const canvas = document.createElement('canvas')
  canvas.width = largeur
  canvas.height = hauteur
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new PieceRefusee('Le navigateur ne peut pas traiter cette image.')
  // Fond blanc : une image PNG transparente deviendrait noire en JPEG.
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, largeur, hauteur)
  ctx.drawImage(source, 0, 0, largeur, hauteur)
  if ('close' in source) source.close()

  const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/jpeg', QUALITE_JPEG))
  if (!blob) throw new PieceRefusee('La conversion de l’image a échoué.')
  return { blob, largeur, hauteur }
}

function chargerImage(fichier: Blob): Promise<HTMLImageElement> {
  return new Promise((ok, ko) => {
    const url = URL.createObjectURL(fichier)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      ok(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      ko(new PieceRefusee('Ce format d’image n’est pas pris en charge par le navigateur.'))
    }
    img.src = url
  })
}

/** Metadonnees seules : la liste des pieces ne doit pas charger les blobs. */
export async function listerPieces(operationId?: Id): Promise<MetaPiece[]> {
  const coll = operationId ? db.pieces.where('operationId').equals(operationId) : db.pieces.toCollection()
  const out: MetaPiece[] = []
  await coll.each((p) => {
    const { blob: _blob, ...meta } = p
    out.push(meta)
  })
  return out.sort((a, b) => a.ajouteeLe.localeCompare(b.ajouteeLe))
}

export async function lirePiece(id: Id): Promise<Piece | undefined> {
  return db.pieces.get(id)
}

export async function lirePieces(ids: readonly Id[]): Promise<Piece[]> {
  return (await db.pieces.bulkGet([...ids])).filter((p): p is Piece => p !== undefined)
}

export async function supprimerPiece(id: Id): Promise<void> {
  await db.pieces.delete(id)
}
